import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import styles from './AuthForm.module.css';
import { NUMBER_ONE } from '../../constants/NumberConstants';
import Model from '../GeneralForm/Modal';
import clientLogger from '../../utils/client-logger';

const Login = ({
  onForgotClick = () => {},
  handleLogin = () => {},
  ipLogin,
  handleLoginIp,
  createComputerCookie,
  isPwdShow,
  handleOnShowPassword,
  isPaygUser,
  handleCloseModel,
  handleOnChangeCostCode,
  cost_code,
}) => {
  const [formVal, setFormVal] = useState({
    username: '',
    password: '',
  });
  const loginContainerRef = useRef(null);
  const [hasRendered, setHasRendered] = useState(false);

  const isPayMessage =
    'To have this session attributed to a specified cost code, please enter a cost code in the field below before logging in.';
  const ispayMessage2 = " Otherwise just click 'Log in' to continue.";
  const ispayMessage3 = 'Log out to end the session for the code.';
  const inputEvent = (event) => {
    const { name, value } = event.target;
    setFormVal((preValue) => ({
      ...preValue,
      [name]: value,
    }));
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingIP, setIsLoadingIP] = useState(false);

  useEffect(
    () => () => {
      setIsLoading(false);
      setIsLoadingIP(false);
    },
    [],
  );

  // White page detection - check if component rendered properly
  useEffect(() => {
    const checkComponentRendering = () => {
      try {
        // Check if required props are present
        const missingProps = [];
        if (typeof handleLogin !== 'function') missingProps.push('handleLogin');
        if (typeof createComputerCookie !== 'function') missingProps.push('createComputerCookie');

        if (missingProps.length > 0) {
          clientLogger.error('Login component missing required props - may cause white page', {
            missingProps,
            componentName: 'AuthForm/Login',
          });
          return;
        }

        // Check if DOM element exists and is visible
        const loginElement = loginContainerRef.current;
        if (!loginElement) {
          clientLogger.error('Login component DOM element not found - white page detected', {
            hasRef: !!loginContainerRef,
            componentName: 'AuthForm/Login',
          });
          return;
        }

        // Check if element has actual content and dimensions
        const rect = loginElement.getBoundingClientRect();
        const hasContent = loginElement.innerHTML && loginElement.innerHTML.trim().length > 0;
        const hasVisibleDimensions = rect.width > 0 && rect.height > 0;
        const isVisible = window.getComputedStyle(loginElement).display !== 'none';

        if (!hasContent || !hasVisibleDimensions || !isVisible) {
          clientLogger.error('Login component rendered but appears blank - white page detected', {
            hasContent,
            hasVisibleDimensions,
            isVisible,
            elementWidth: rect.width,
            elementHeight: rect.height,
            innerHTML: loginElement.innerHTML.substring(0, 100), // First 100 chars for debugging
            componentName: 'AuthForm/Login',
          });
          return;
        }

        // Check if CSS styles are loaded
        const computedStyles = window.getComputedStyle(loginElement);
        const hasStyles = computedStyles.fontFamily !== 'initial' || computedStyles.fontSize !== 'initial';

        if (!hasStyles) {
          clientLogger.warn('Login component CSS styles may not be loaded - potential styling issue', {
            fontFamily: computedStyles.fontFamily,
            fontSize: computedStyles.fontSize,
            componentName: 'AuthForm/Login',
          });
        }

        // Component appears to be rendering correctly
        setHasRendered(true);
        clientLogger.info('Login component rendered successfully', {
          elementWidth: rect.width,
          elementHeight: rect.height,
          hasContent: true,
          componentName: 'AuthForm/Login',
        });
      } catch (error) {
        clientLogger.error('Error during white page detection check', {
          error: error.message,
          componentName: 'AuthForm/Login',
        });
      }
    };

    // Check immediately and then after a small delay to ensure everything is rendered
    const timeoutId = setTimeout(checkComponentRendering, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only white-page diagnostics
  }, []);

  // Additional check for delayed rendering issues
  useEffect(() => {
    if (!hasRendered) {
      const timeoutId = setTimeout(() => {
        if (!hasRendered) {
          clientLogger.error('Login component failed to render within 2 seconds - white page likely', {
            componentName: 'AuthForm/Login',
            timeoutMs: 2000,
          });
        }
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [hasRendered]);

  const loginSubmit = async (events) => {
    events.preventDefault();
    setIsLoading(true);

    try {
      const handleLoginResp = await handleLogin(formVal);

      if (handleLoginResp.payload === false) {
        clientLogger.loginFailure(new Error('Login response returned false'), {
          loginType: 'standard',
        });
        setIsLoading(false);
      }
      if (handleLoginResp.payload.isValid === true) {
        clientLogger.loginSuccess(formVal.username);
        await createComputerCookie(handleLoginResp.payload.userid);
      }
      if (handleLoginResp.payload.isValid === false) {
        clientLogger.loginFailure(new Error(handleLoginResp.payload.message || 'Invalid credentials'), {
          loginType: 'standard',
        });
        setIsLoading(false);
      }
    } catch (error) {
      clientLogger.loginFailure(error, {
        loginType: 'standard',
        errorPhase: 'submit',
      });
      setIsLoading(false);
    }
  };

  const loginSubmit_costCode = async (events) => {
    events.preventDefault();
    setIsLoading(true);
    const data = { ...formVal, costCode: cost_code };
    const handleLoginResp = await handleLogin(data);
    if (handleLoginResp.payload.isValid === true) {
      await createComputerCookie(handleLoginResp.payload.userid);
    }
    if (handleLoginResp.payload.isValid === false) {
      setIsLoading(false);
    }
  };

  const loginIPSubmit = async (events) => {
    events.preventDefault();
    setIsLoadingIP(true);
    const handleLoginResp = await handleLoginIp();
    if (handleLoginResp.payload === false) {
      setIsLoading(false);
    }
    if (handleLoginResp.payload.isValid === true) {
      await createComputerCookie(handleLoginResp.payload.userid);
    }
    if (handleLoginResp.payload.isValid === false) {
      setIsLoadingIP(false);
    }
  };

  return (
    <div ref={loginContainerRef} className={styles.login}>
      {isPaygUser && (
        <Model
          show={isPaygUser}
          isLoginButtonFooter
          handleClose={() => {
            handleCloseModel();
            setIsLoading(false);
          }}
          handleInputChange={handleOnChangeCostCode}
          handleOnClick={loginSubmit_costCode}
          title="Cost Code Required"
          isInfo={true}
          className="loginCode"
          InputValue={cost_code}
        >
          <h5>
            {isPayMessage}
            <br />
            {ispayMessage2}
            <br />
            {ispayMessage3}
          </h5>
        </Model>
      )}
      <form action="" method="get" onSubmit={loginSubmit} className={styles.animatebottom}>
        <fieldset className={styles.clearfix}>
          <h1>
            Sign-in to <br />
            Market Intelligence
          </h1>
          <h2 className="float-start mb-2">Enter your details to sign in:</h2>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                className="bi bi-person-fill"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                />
              </svg>
            </span>
            <input
              type="email"
              name="username"
              onChange={inputEvent}
              className="form-control"
              placeholder="Email"
              aria-label="Email"
              aria-describedby="basic-addon1"
              required
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon2">
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                className="bi bi-lock-fill"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2.5 9a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V9z" />
                <path fillRule="evenodd" d="M4.5 4a3.5 3.5 0 1 1 7 0v3h-1V4a2.5 2.5 0 0 0-5 0v3h-1V4z" />
              </svg>
            </span>
            <input
              type={isPwdShow ? 'text' : 'password'}
              name="password"
              onChange={inputEvent}
              className="form-control"
              placeholder="Password"
              aria-label="Password"
              aria-describedby="basic-addon2"
              required
            />

            <div
              className="passwordEye"
              onClick={() => {
                handleOnShowPassword();
              }}
            >
              {!isPwdShow ? <i className="bi bi-eye-fill" /> : <i className="bi bi-eye-slash-fill" />}
            </div>
          </div>
          <div className="mb-3">
            <span onClick={onForgotClick} className="float-start">
              <button type="button" className="btn btn-link">
                Forgot password?
              </button>
            </span>
            <span className="float-end ">
              {isLoading ? (
                <button className="btn btn-primary" type="button" disabled>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                </button>
              ) : (
                <button className="btn btn-primary" type="submit" disabled={formVal.disabled}>
                  Sign in
                </button>
              )}
            </span>
          </div>
          <div className="float-end buttonIPLoginSpacing">
            {ipLogin !== undefined && ipLogin.Ok === NUMBER_ONE && !isLoadingIP && (
              <button type="button" className="btn btn-primary" onClick={loginIPSubmit} disabled={formVal.disabled}>
                {`${ipLogin.Company_name} Company Log In`}
              </button>
            )}
            {ipLogin !== undefined && ipLogin.Ok === NUMBER_ONE && isLoadingIP && (
              <button className="btn btn-primary" type="button" disabled>
                {`${ipLogin.Company_name} Company Log In`}{' '}
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              </button>
            )}
          </div>
        </fieldset>
      </form>
    </div>
  );
};

Login.propTypes = {
  handleLogin: PropTypes.func,
  onForgotClick: PropTypes.func,
  userDetails: PropTypes.any,
};

export default React.memo(Login);
